import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { usePageTitle } from '../lib/usePageTitle';

interface Recipe {
  id: number;
  title: string;
  description: string;
  servings: number;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  source_url?: string;
  source_name?: string;
  notes?: string;
  current_ingredients?: string[];
  current_instructions?: string[];
  current_sections?: any[];
  has_sections?: boolean;
}

export default function RecipePrint() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(recipe ? `${recipe.title} - Print` : 'Recipe Print');

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/recipes/${id}/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setRecipe(response.data);
        
        // Auto-print after recipe loads
        setTimeout(() => {
          window.print();
        }, 500);
        
      } catch (err: any) {
        console.error('Error fetching recipe:', err);
        setError(err.response?.data?.detail || 'Failed to load recipe');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRecipe();
    }
  }, [id]);

  const formatTime = (minutes?: number) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        Loading recipe...
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1>Recipe Not Found</h1>
          <p>{error || 'Recipe could not be loaded'}</p>
        </div>
      </div>
    );
  }

  const printStyles = `
    @media print {
      body { margin: 0; padding: 20px; }
      .no-print { display: none !important; }
      .print-break { page-break-before: always; }
      h1 { font-size: 24pt; margin-bottom: 12pt; }
      h2 { font-size: 18pt; margin: 16pt 0 8pt 0; }
      h3 { font-size: 14pt; margin: 12pt 0 6pt 0; }
      p, li { font-size: 11pt; line-height: 1.4; }
      .recipe-info { margin-bottom: 16pt; }
      .ingredients, .instructions { margin-bottom: 16pt; }
      .ingredients ul, .instructions ol { margin: 8pt 0; padding-left: 24pt; }
      .ingredients li, .instructions li { margin-bottom: 4pt; }
    }
    @media screen {
      .no-print { margin-bottom: 20px; text-align: center; }
      button { 
        padding: 10px 20px; 
        margin: 0 5px; 
        background: #2563eb; 
        color: white; 
        border: none; 
        border-radius: 4px; 
        cursor: pointer; 
        font-size: 14px;
      }
      button:hover { background: #1d4ed8; }
    }
  `;

  return (
    <>
      <style>{printStyles}</style>
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto', 
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        lineHeight: '1.6',
        color: '#333'
      }}>
        {/* Print controls - hidden when printing */}
        <div className="no-print">
          <button onClick={() => window.print()}>
            🖨️ Print Recipe
          </button>
          <button onClick={() => window.close()}>
            ✕ Close
          </button>
        </div>

        {/* Recipe header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            marginBottom: '8px',
            color: '#1f2937'
          }}>
            {recipe.title}
          </h1>
          
          {recipe.description && (
            <p style={{ 
              fontSize: '16px', 
              color: '#6b7280', 
              fontStyle: 'italic',
              marginBottom: '16px'
            }}>
              {recipe.description}
            </p>
          )}

          {/* Recipe info */}
          <div className="recipe-info" style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '16px',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <div>
              <strong>Servings:</strong> {recipe.servings}
            </div>
            {recipe.prep_time_minutes && (
              <div>
                <strong>Prep Time:</strong> {formatTime(recipe.prep_time_minutes)}
              </div>
            )}
            {recipe.cook_time_minutes && (
              <div>
                <strong>Cook Time:</strong> {formatTime(recipe.cook_time_minutes)}
              </div>
            )}
            {recipe.total_time_minutes && (
              <div>
                <strong>Total Time:</strong> {formatTime(recipe.total_time_minutes)}
              </div>
            )}
          </div>

          {recipe.source_name && (
            <p style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              marginBottom: '16px' 
            }}>
              <strong>Source:</strong> {recipe.source_name}
            </p>
          )}
        </div>

        {/* Recipe content */}
        {recipe.has_sections && recipe.current_sections ? (
          // Multi-section recipe
          recipe.current_sections.map((section: any, sectionIndex: number) => (
            <div key={sectionIndex} style={{ marginBottom: '32px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                marginBottom: '16px',
                color: '#1f2937',
                borderBottom: '1px solid #d1d5db',
                paddingBottom: '8px'
              }}>
                {section.name}
              </h2>
              
              {section.ingredients && section.ingredients.length > 0 && (
                <div className="ingredients" style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    Ingredients
                  </h3>
                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    {section.ingredients.map((ingredient: string, idx: number) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>
                        {ingredient}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {section.instructions && section.instructions.length > 0 && (
                <div className="instructions">
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    Instructions
                  </h3>
                  <ol style={{ margin: '0', paddingLeft: '20px' }}>
                    {section.instructions.map((instruction: string, idx: number) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>
                        {instruction}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))
        ) : (
          // Single-section recipe
          <>
            {recipe.current_ingredients && recipe.current_ingredients.length > 0 && (
              <div className="ingredients" style={{ marginBottom: '32px' }}>
                <h2 style={{ 
                  fontSize: '20px', 
                  fontWeight: '600', 
                  marginBottom: '16px',
                  color: '#1f2937',
                  borderBottom: '1px solid #d1d5db',
                  paddingBottom: '8px'
                }}>
                  Ingredients
                </h2>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {recipe.current_ingredients.map((ingredient: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: '6px', fontSize: '14px' }}>
                      {ingredient}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {recipe.current_instructions && recipe.current_instructions.length > 0 && (
              <div className="instructions" style={{ marginBottom: '32px' }}>
                <h2 style={{ 
                  fontSize: '20px', 
                  fontWeight: '600', 
                  marginBottom: '16px',
                  color: '#1f2937',
                  borderBottom: '1px solid #d1d5db',
                  paddingBottom: '8px'
                }}>
                  Instructions
                </h2>
                <ol style={{ margin: '0', paddingLeft: '20px' }}>
                  {recipe.current_instructions.map((instruction: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: '12px', fontSize: '14px' }}>
                      {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}

        {/* Notes */}
        {recipe.notes && (
          <div style={{ 
            marginTop: '32px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Notes
            </h3>
            <p style={{ fontSize: '14px', lineHeight: '1.6' }}>
              {recipe.notes}
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={{ 
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '12px',
          color: '#9ca3af',
          textAlign: 'center'
        }}>
          <p>
            Printed from MagicFamConcierge on {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </>
  );
}